import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import MergeIcons from '@/icons/MergeIcons'
import {
    getInvoiceColumns,
    getInvoiceFilterItems,
    getInvoiceItemsColumns,
} from '@/modules/Customer/utils/customer-invoices-config'
import { getCustomerInvoices, mergeInvoices } from '@/modules/Customer/services'
import type { CustomerInvoice } from '@/modules/Customer/types'
import { formatCurrency } from '@/utils/function'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

/**
 * Filters interface for Customer Invoices
 */
interface InvoiceFilters {
    service_id: number[]
    invoice_status: string[]
}

/**
 * Business logic hook for Customer Invoices
 * Manages data fetching, pagination, sorting, and merge functionality
 */
export const useCustomerInvoices = () => {
    const { id } = useParams()
    const { t } = useTranslation('customer')
    const { setConfirmation } = useConfirmation()

    // URL params for pagination, sorting and filters with prefix to avoid conflicts
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<InvoiceFilters>({
        defaults: { sort_by: 'invoice_id', sort_order: 'desc', page_size: 10 },
        filterDefaults: {
            service_id: [],
            invoice_status: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<InvoiceFilters>({
        defaultValues: filters,
    })

    // Query client callback for refetching related data
    const { refetch } = useQueryClientCallback({
        pattern: 'customers',
        mode: 'contains',
    })

    // Fetch invoices data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.INVOICES, id, queryParams],
        queryFn: () =>
            getCustomerInvoices({
                ...queryParams,
                customerID: Number(id),
            }),
        placeholderData: true,
    })

    // Selection state for merge functionality
    const [selectedInvoices, setSelectedInvoices] = useState<string[]>([])

    // Merge invoices mutation
    const { mutateAsync: mergeInvoicesMutation, isPending: isMerging } =
        useMutationWithHandler(
            { mutationFn: mergeInvoices },
            {
                reactQueryKeys: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.INVOICES],
                successMessage: t('merge-invoices-success'),
                errorMessage: t('merge-invoices-error'),
                onSuccess: () => {
                    setSelectedInvoices([])
                    refetch()
                },
            }
        )

    // Handle merge invoices with confirmation
    const handleMergeInvoices = useCallback(
        (selectedRows: CustomerInvoice[]) => {
            const invoiceIds = selectedRows.map((row) => row.invoice_id)

            // Reuse invoice columns from config for preview
            const baseColumns = getInvoiceColumns(t)

            const totalAmount = selectedRows.reduce(
                (sum, inv) => sum + (inv.invoice_total || 0),
                0
            )

            // Build invoice items preview with DataTableCustomized
            const customContent = (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                    <DataTableCustomized<CustomerInvoice>
                        columns={baseColumns}
                        rows={selectedRows}
                        size="small"
                        rowKey={(row, index) => `${row.invoice_id}-${index}`}
                        emptyMessage={t('no_items', 'Không có items')}
                    />

                    {/* Total summary footer */}
                    <div className="bg-indigo-50 px-4 py-2.5 border-t-2 border-indigo-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-indigo-900">
                                {t('total')}
                            </span>
                            <span className="text-base font-bold text-indigo-900">
                                {formatCurrency(totalAmount, 'VND')}
                            </span>
                        </div>
                    </div>
                </div>
            )

            setConfirmation({
                open: true,
                title: t('merge-invoices-confirm-title'),
                description: t('merge-invoices-confirm-description', {
                    count: invoiceIds.length,
                    invoice_ids: invoiceIds.join(', '),
                }),
                customContent,
                onConfirm: () => mergeInvoicesMutation(invoiceIds),
            })
        },
        [mergeInvoicesMutation, setConfirmation, t]
    )

    // Memoized columns configuration
    const columns = useMemo(() => getInvoiceColumns(t), [t])

    // Memoized invoice items columns for expandable rows
    const invoiceItemsColumns = useMemo(() => getInvoiceItemsColumns(t), [t])

    // Memoized filter configuration
    const filterItems = useMemo(
        () => getInvoiceFilterItems(Number(id), t),
        [id, t]
    )

    // Selection actions
    const selectionActions = useMemo(
        () => [
            {
                key: 'merge',
                label: t('merge-invoices'),
                icon: <MergeIcons />,
                onClick: handleMergeInvoices,
                disabled: (rows: CustomerInvoice[]) =>
                    rows.length < 2 || isMerging,
                variant: 'info' as const,
            },
        ],
        [t, handleMergeInvoices, isMerging]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    const handleSelectionChange = useCallback((keys: string[]) => {
        setSelectedInvoices(keys)
    }, [])

    return {
        // Data
        invoices: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        searchInputProps,

        // Configs
        filters,
        control,
        setFilter,
        columns,
        selectionActions,

        // Selection
        invoiceItemsColumns,
        selectedInvoices,
        filterItems,
        handleSelectionChange,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}
